/*
 * Copyright (c) 2016-present Invertase Limited & Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this library except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
import {
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useSubscription } from "@utils/firebaseAuth/query/auth/useSubscription";
import type { Auth, AuthError, User } from "firebase/auth";
import type { DevicePorts } from "../../../../runtime/ports";

export function useFirebaseAuthUser<R = User>(
  queryKey: QueryKey,
  auth: Auth | DevicePorts["auth"],
  options?: Omit<UseQueryOptions<User, AuthError, R>, "queryFn">,
): UseQueryResult<R, AuthError> {
  const subscribeFn = (cb: (user: User | null) => Promise<void>) =>
    "subscribe" in auth
      ? auth.subscribe(cb)
      : auth.onAuthStateChanged(cb);

  return useSubscription<User, AuthError, R>(queryKey, subscribeFn, options);
}
